import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import ReactDOM from 'react-dom';
import Svg, { Path } from 'react-native-svg';
import { tokens } from '@design';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';

export interface DropdownOption {
  value: string;
  label: string;
  /** Nesting level, e.g. a section under its department; indents the row. */
  depth?: number;
}

// A nested row keeps the normal row padding and adds this much per level.
const ROW_PADDING = tokens.spacing.absolute['3'];
const INDENT_PER_LEVEL = 20;

export interface MultiSelectDropdownProps {
  values: string[];
  options: DropdownOption[];
  placeholder: string;
  onChange: (values: string[]) => void;
  disabled?: boolean;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /** Shown in the opened menu when there are no options. Defaults to common.noData. */
  emptyText?: string;
}

// hitSlop is a RN touch-target API (top/bottom/left/right only, no start/end
// equivalent), not a style object; the shared physicalKeySelector unintentionally
// matches bare `right:`/`left:` keys file-wide because comma-joining alternatives
// inside the selector template literal breaks the intended CallExpression/
// JSXAttribute descendant scoping.
// eslint-disable-next-line no-restricted-syntax
const HIT_SLOP_4 = { top: 4, bottom: 4, left: 4, right: 4 };

const ChevronDownIcon = ({ size = 16, color = '#666' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9l6 6 6-6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const MultiSelectDropdown = ({ values, options, placeholder, onChange, disabled, emptyActionLabel, onEmptyAction, emptyText }: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const { isRTL, t } = useLanguage();
  const styles = getStyles(isRTL);

  const selectedOptions = options.filter(opt => values.includes(opt.value));

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray['500'] : tokens.colors.raw.gray['400'],
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate['100'],
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : tokens.colors.raw.slate['100'],
  };

  const handleOpen = () => {
    if (disabled) return;
    // Empty + CTA provided -> fire the action instead of opening a menu.
    if (options.length === 0 && emptyActionLabel && onEmptyAction) {
      onEmptyAction();
      return;
    }
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const toggleOption = (optionValue: string) => {
    if (values.includes(optionValue)) {
      onChange(values.filter(v => v !== optionValue));
    } else {
      onChange([...values, optionValue]);
    }
  };

  const removeOption = (optionValue: string) => {
    onChange(values.filter(v => v !== optionValue));
  };

  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      ref={menuCallbackRef as any}
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        start: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: tokens.spacing.absolute['px'],
        borderColor: dropdownTheme.menuBorder,
        borderRadius: tokens.radii.absolute['radius-12'],
        maxHeight: 250,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'hidden' as any,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
        <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
          {options.length === 0 && (
            <View style={styles.dropdownItem}>
              <Text style={[styles.dropdownItemText, { color: dropdownTheme.placeholder }]}>
                {emptyText ?? t('common.noData')}
              </Text>
            </View>
          )}
          {options.map((option) => {
            const isSelected = values.includes(option.value);
            return (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.dropdownItem,
                  { borderBottomColor: dropdownTheme.itemBorder },
                  // Indent from the side the text starts on (as the sidebar does).
                  option.depth
                    ? (isRTL
                      ? { paddingEnd: ROW_PADDING + option.depth * INDENT_PER_LEVEL }
                      : { paddingStart: ROW_PADDING + option.depth * INDENT_PER_LEVEL })
                    : null,
                ]}
                onPress={() => toggleOption(option.value)}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <View style={{
                    width: 20, height: 20, borderRadius: tokens.radii.absolute['radius-4'],
                    borderWidth: 2, borderColor: isSelected ? tokens.colors.semantic.general['Main Color'] : tokens.colors.raw.gray['300'],
                    backgroundColor: isSelected ? tokens.colors.semantic.general['Main Color'] : 'transparent',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && (
                      <Svg width={12} height={12} viewBox="0 0 24 24">
                        <Path d="M20 6L9 17l-5-5" stroke={tokens.colors.raw.white} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </Svg>
                    )}
                  </View>
                  <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, flex: 1 }]}>{option.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>,
    document.body
  ) : null;

  const showEmptyCta = options.length === 0 && !!emptyActionLabel && !!onEmptyAction;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border, minHeight: 44 },
          disabled && { backgroundColor: dropdownTheme.disabledBg },
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        {showEmptyCta ? (
          <Text style={[styles.dropdownButtonText, { color: tokens.colors.semantic.general['Main Color'] }]}>
            + {emptyActionLabel}
          </Text>
        ) : selectedOptions.length === 0 ? (
          <Text style={[styles.dropdownButtonText, { color: dropdownTheme.placeholder }]}>
            {placeholder}
          </Text>
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: tokens.spacing.absolute['1'], flex: 1, justifyContent: 'flex-end' }}>
            {selectedOptions.map(opt => (
              <View key={opt.value} style={{
                flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute['1'],
                backgroundColor: isDark ? tokens.colors.raw.gray['700'] : tokens.colors.raw.slate['100'],
                borderRadius: tokens.radii.absolute['radius-6'], paddingHorizontal: tokens.spacing.absolute['2'], paddingVertical: tokens.spacing.absolute['1'],
              }}>
                <TouchableOpacity onPress={() => removeOption(opt.value)} hitSlop={HIT_SLOP_4}>
                  <Text style={{ color: tokens.colors.raw.red['500'], fontSize: tokens.typography.paragraph['paragraph small font size'], fontFamily: FONTS.regular }}>×</Text>
                </TouchableOpacity>
                <Text style={{ color: dropdownTheme.text, fontSize: tokens.typography.paragraph['paragraph extra-small font size'], fontFamily: FONTS.regular }}>{opt.label}</Text>
              </View>
            ))}
          </View>
        )}
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

const getStyles = (isRTL: boolean) => StyleSheet.create({
  dropdownContainer: { position: 'relative', zIndex: 1000 },
  dropdownButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute['px'], borderColor: tokens.colors.raw.slate['200'],
    borderRadius: tokens.radii.absolute['radius-8'],
    paddingHorizontal: tokens.spacing.absolute['3'], paddingVertical: 7.5,
    minHeight: 36,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownButtonText: {
    flex: 1, fontSize: tokens.typography.paragraph['paragraph small font size'],
    fontFamily: FONTS.regular, color: tokens.colors.semantic.general.foreground, textAlign: getTextAlign(isRTL),
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute['3'], paddingVertical: tokens.spacing.absolute['2_5'],
    borderBottomWidth: tokens.spacing.absolute['px'], borderBottomColor: tokens.colors.raw.slate['100'],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph['paragraph small font size'],
    fontFamily: FONTS.regular, color: tokens.colors.semantic.general.foreground, textAlign: getTextAlign(isRTL),
  },
});

export default MultiSelectDropdown;
